import {useState} from "react";
import {useGetCurrentSystemQuery} from "@/store/systems.ts";
import {PageWrapper} from "@/layout/components/PageWrapper.tsx";
import {PageHeader} from "@/layout/components/PageHeader.tsx";
import {toast} from "sonner";

import {UserSearchCombobox} from "@/features/user-directory/components/UserSearchCombobox";
import {CreateUserModal, CreateUserModalCreatedInfo} from "@/features/user-directory/components/CreateUserModal";
import {LearningUserDetail, LearningUserSearchResult} from "@/features/user-directory/types";
import {useGetUserDetailQuery} from "@/features/user-directory/api/userDirectoryApi";

import {SearchModeToggle, SearchMode} from "./components/SearchModeToggle";
import {ChipSearchBar} from "./components/ChipSearchBar";
import {CreateChipModal} from "./components/CreateChipModal";
import {UserResultCard} from "./components/UserResultCard";
import {ChipResultCard} from "./components/ChipResultCard";
import {AssignConflictDialog} from "./components/AssignConflictDialog";
import {Button} from "@/components/ui/button.tsx";

import {
    useAssignChipMutation,
    useEnsureTicketUserMutation,
    useGetChipDetailQuery,
    useGetUserChipsQuery,
} from "@/store/chipAssignmentApi.ts";
import {ChipSearchResult, isAssignChipConflict} from "@/types/chipAssignment";

interface AssignConflictState {
    chipId: number;
    userId: number;
    existingOwnerName: string | null;
}

export function ChipUserAssignmentPage() {
    const {data: system} = useGetCurrentSystemQuery();
    const isPlanOk = String(system?.subscription_code) === String(3); // 3 = מרכזיות

    const [mode, setMode] = useState<SearchMode>('user');
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [selectedChipId, setSelectedChipId] = useState<number | null>(null);

    const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
    const [createChipModalOpen, setCreateChipModalOpen] = useState(false);
    const [assignConflict, setAssignConflict] = useState<AssignConflictState | null>(null);

    const {data: userDetail} = useGetUserDetailQuery(selectedUserId as number, {skip: !selectedUserId});
    const {data: userChips = [], isFetching: isLoadingUserChips} = useGetUserChipsQuery(selectedUserId as number, {skip: !selectedUserId});
    const {data: chipDetail} = useGetChipDetailQuery(selectedChipId as number, {skip: !selectedChipId});

    const [ensureTicketUser] = useEnsureTicketUserMutation();
    const [assignChip, {isLoading: isAssigning}] = useAssignChipMutation();

    const disabled = !isPlanOk;

    function handleModeChange(newMode: SearchMode) {
        setMode(newMode);
        setSelectedUserId(null);
        setSelectedChipId(null);
        setAssignConflict(null);
    }

    async function handleAssign(chipId: number, userId: number, force = false) {
        try {
            const result = await assignChip({chip_id: chipId, user_id: userId, force}).unwrap();
            if (isAssignChipConflict(result)) {
                setAssignConflict({chipId, userId, existingOwnerName: result.existing_owner.name});
                return;
            }
            toast.success("הכרטיס שויך בהצלחה");
            setAssignConflict(null);
            setSelectedUserId(userId);
            setSelectedChipId(chipId);
        } catch (err: unknown) {
            const msg = (err as {data?: {msg?: string}})?.data?.msg || "שגיאה בשיוך הכרטיס";
            toast.error(msg);
        }
    }

    async function handleUserCreated(user: LearningUserDetail, info: CreateUserModalCreatedInfo) {
        try {
            await ensureTicketUser({
                learning_user_id: user.id,
                first_name: info.firstName,
                last_name: info.lastName,
                phone: info.phone,
            }).unwrap();
        } catch (err: unknown) {
            const msg = (err as {data?: {msg?: string}})?.data?.msg || "שגיאה ביצירת מזמין מקושר";
            toast.error(msg);
        }

        if (mode === 'chip' && selectedChipId) {
            handleAssign(selectedChipId, user.id);
        } else {
            setSelectedUserId(user.id);
        }
    }

    function handleChipCreated(chip: {id: number}) {
        if (mode === 'user' && selectedUserId) {
            handleAssign(chip.id, selectedUserId);
        } else {
            setSelectedChipId(chip.id);
        }
    }

    function handleSelectExistingUser(user: LearningUserSearchResult) {
        setSelectedUserId(user.id);
    }

    function handleSelectChipToAssignFromUser(chip: ChipSearchResult) {
        if (!selectedUserId) return;
        handleAssign(chip.id, selectedUserId);
    }

    function handleSelectExistingChip(chip: ChipSearchResult) {
        setSelectedChipId(chip.id);
    }

    function handleSelectUserToAssignFromChip(user: LearningUserSearchResult) {
        if (!selectedChipId) return;
        handleAssign(selectedChipId, user.id);
    }

    return (
        <PageWrapper>
            <PageHeader title="שיוך כרטיס למשתמש" />

            {!isPlanOk && system !== undefined && (
                <div className="rounded-lg border border-yellow-400 bg-yellow-50 p-4 text-yellow-800 text-base">
                    פיצ'ר זה זמין במסלול <strong>מרכזיות</strong> בלבד. לשדרוג פנה אלינו.
                </div>
            )}

            <SearchModeToggle mode={mode} onChange={handleModeChange} disabled={disabled} />

            {mode === 'user' && (
                <div className="space-y-4 max-w-2xl w-full">
                    <UserSearchCombobox key={mode} onSelect={handleSelectExistingUser} disabled={disabled} />
                    <Button
                        type="button"
                        variant="outline"
                        className="text-base"
                        onClick={() => setCreateUserModalOpen(true)}
                        disabled={disabled}
                    >
                        + צור משתמש חדש
                    </Button>

                    {userDetail && selectedUserId === userDetail.id && (
                        <UserResultCard
                            key={userDetail.id}
                            user={userDetail}
                            chips={userChips}
                            isLoadingChips={isLoadingUserChips}
                            onSelectChipToAssign={handleSelectChipToAssignFromUser}
                            onCreateChip={() => setCreateChipModalOpen(true)}
                            disabled={disabled || isAssigning}
                        />
                    )}
                </div>
            )}

            {mode === 'chip' && (
                <div className="space-y-4 max-w-2xl w-full">
                    <ChipSearchBar key={mode} onSelect={handleSelectExistingChip} disabled={disabled} />
                    <Button
                        type="button"
                        variant="outline"
                        className="text-base"
                        onClick={() => setCreateChipModalOpen(true)}
                        disabled={disabled}
                    >
                        + צור כרטיס חדש
                    </Button>

                    {chipDetail && selectedChipId === chipDetail.id && (
                        <ChipResultCard
                            key={chipDetail.id}
                            chip={chipDetail}
                            onSelectUserToAssign={handleSelectUserToAssignFromChip}
                            onCreateUser={() => setCreateUserModalOpen(true)}
                            disabled={disabled || isAssigning}
                        />
                    )}
                </div>
            )}

            <CreateUserModal
                open={createUserModalOpen}
                onOpenChange={setCreateUserModalOpen}
                onCreated={handleUserCreated}
            />
            <CreateChipModal
                open={createChipModalOpen}
                onOpenChange={setCreateChipModalOpen}
                onCreated={handleChipCreated}
            />
            <AssignConflictDialog
                open={Boolean(assignConflict)}
                existingOwnerName={assignConflict?.existingOwnerName ?? null}
                isLoading={isAssigning}
                onCancel={() => setAssignConflict(null)}
                onConfirm={() => {
                    if (assignConflict) {
                        handleAssign(assignConflict.chipId, assignConflict.userId, true);
                    }
                }}
            />
        </PageWrapper>
    );
}
